import React from "react";
import { graphql } from "gatsby";
import { useTheme } from "@emotion/react";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";

import { Box, Hero, Seo } from "components";
import { PrismicColor } from "utils";

import { AccordionGroup } from "slices/accordion-group";
import { Calendar } from "slices/calendar";
import { CallToAction } from "slices/call-to-action";
import { DetailList } from "slices/detail-list";
import { FinancialReports } from "slices/financial-reports";
import { FullWidthImage } from "slices/full-width-image";
import { ImageTextSplit } from "slices/image-text-split";
import { NewsFeed } from "slices/news-feed";
import { PeopleList } from "slices/people-list";
import { RichText } from "slices/rich-text";
import { PageDivider } from "slices/page-divider";

const returnSlice = ({ i, items, NEWS, primary, slice_type }) => {
  switch (slice_type) {
    case "accordion_group":
      return (
        <AccordionGroup {...primary} items={items} key={`accordion-${i}`} />
      );
    case "calendar":
      return <Calendar {...primary} items={items} key={`calendar-${i}`} />;
    case "call_to_action":
      return (
        <CallToAction {...primary} items={items} key={`callToAction-${i}`} />
      );
    case "detail_list":
      return <DetailList {...primary} items={items} key={`detailList-${i}`} />;
    case "divider":
      return <PageDivider {...primary} key={`divider-${i}`} />;
    case "financial_reports":
      return (
        <FinancialReports
          {...primary}
          items={items}
          key={`financialReports-${i}`}
        />
      );
    case "full_width_image":
      return (
        <FullWidthImage {...primary} {...items} key={`fullWidthImage-${i}`} />
      );
    case "image-text_split":
      return <ImageTextSplit {...primary} key={`imageTextSplit-${i}`} />;
    case "news_section":
      return <NewsFeed items={NEWS} {...primary} key={`newsFeed-${i}`} />;
    case "people_list":
      return <PeopleList {...primary} items={items} key={`peopleList-${i}`} />;
    case "text":
      return <RichText {...primary} items={items} key={`richText-${i}`} />;
    default:
      return null;
  }
};

function PageTemplate({ data }) {
  const THEME = useTheme();
  // `withPrismicPreview` merges live preview data into `data` automatically when
  // viewed in a Prismic preview session; otherwise this is the build-time data.
  const PAGE = data.prismicPage.data;
  const NEWS = data.allPrismicArticle.nodes;

  if (PAGE.body && PAGE.body[0]) {
    PAGE.body.forEach((item, index) => {
      if (
        item &&
        item.primary &&
        item.primary.background !== "White" &&
        (!PAGE.body[index - 1] ||
          PAGE.body[index - 1].primary.background !== item.primary.background)
      ) {
        item.primary.topAngle = true;
      }
      if (
        item &&
        item.primary &&
        item.primary.background !== "White" &&
        (!PAGE.body[index + 1] ||
          PAGE.body[index + 1].primary.background !== item.primary.background)
      ) {
        item.primary.bottomAngle = true;
      }
    });
  }

  return (
    <>
      {/* Hero */}
      <Hero {...PAGE} />

      {/* Slices */}
      {PAGE.body &&
        PAGE.body[0] &&
        PAGE.body[0].slice_type &&
        PAGE.body.map(({ slice_type, primary, items }, i) => {
          return (
            <Box key={"pageBodySlice" + i}>
              {/* Solid angle scrim */}
              {primary.topAngle && (
                <Box
                  width="100%"
                  pb="10%"
                  css={{
                    background: `linear-gradient(-4deg, ${
                      THEME.colors.background[
                        PrismicColor(primary.background).split(".")[1]
                      ]
                    } 49.5%, transparent 50%)`,
                  }}
                />
              )}
              {returnSlice({ i, slice_type, primary, items, NEWS })}
              {/* Solid angle scrim */}
              {primary.bottomAngle && (
                <Box
                  width="100%"
                  pb="10%"
                  css={{
                    transform: "rotate(180deg)",
                    background: `linear-gradient(-4deg, ${
                      THEME.colors.background[
                        PrismicColor(primary.background).split(".")[1]
                      ]
                    } 49.5%, transparent 50%)`,
                  }}
                />
              )}
            </Box>
          );
        })}
    </>
  );
}

// Access Prismic data, to feed into page.
export const query = graphql`
  query PageQuery($uid: String!) {
    allPrismicArticle(sort: { data: { date: DESC } }) {
      nodes {
        data {
          title {
            text
          }
          excerpt
          link {
            url
          }
          date(formatString: "MMM DD, YYYY")
        }
      }
    }
    prismicPage(uid: { eq: $uid }) {
      _previewable
      data {
        hero_image {
          gatsbyImageData(
            layout: FULL_WIDTH
            breakpoints: [750, 1080, 1366, 1920, 2560, 3840]
            imgixParams: { q: 90 }
          )
          alt
          url
        }
        label {
          text
        }
        headline {
          text
        }
        introduction {
          html
        }
        button_label
        button_link {
          url
        }
        quick_links {
          quick_link
          external_link {
            type
            uid
            url
          }
        }
        body {
          ... on PrismicPageDataBodyAccordionGroup {
            slice_type
            primary {
              background
              title {
                text
              }
              content {
                html
              }
            }
            items {
              title {
                text
              }
              featured
              accordion_content {
                html
              }
              accordion_highlight
            }
          }
          ... on PrismicPageDataBodyDetailList {
            slice_type
            primary {
              background
              title {
                text
              }
            }
            items {
              title {
                text
              }
              description
            }
          }
          ... on PrismicPageDataBodyText {
            id
            slice_type
            primary {
              background
              title {
                text
              }
              direction
            }
            items {
              content {
                html
              }
            }
          }
          ... on PrismicPageDataBodyDivider {
            slice_type
            primary {
              background
            }
          }
          ... on PrismicPageDataBodyFinancialReports {
            id
            slice_type
            primary {
              background
              title {
                text
              }
            }
            items {
              year
              q1_report {
                url
              }
              q2_report {
                url
              }
              q3_report {
                url
              }
              q4_report {
                url
              }
              aif {
                url
              }
            }
          }
          ... on PrismicPageDataBodyImageTextSplit {
            id
            slice_type
            primary {
              background
              button_label
              button_link {
                url
              }
              color
              content {
                html
              }
              image {
                gatsbyImageData(width: 2100)
                alt
                url
              }
              invert
              title {
                text
              }
            }
          }
          ... on PrismicPageDataBodyCallToAction {
            id
            slice_type
            primary {
              background
              content {
                html
              }
              image {
                gatsbyImageData(width: 2100)
                alt
                url
              }
              title {
                text
              }
            }
            items {
              button_label
              button_link {
                url
              }
            }
          }
          ... on PrismicPageDataBodyFullWidthImage {
            slice_type
            primary {
              background
              image {
                gatsbyImageData(width: 2100)
                alt
                url
              }
              width
            }
          }
          ... on PrismicPageDataBodyNewsSection {
            slice_type
            primary {
              title {
                text
              }
              background
              recent_only
            }
          }
          ... on PrismicPageDataBodyPeopleList {
            slice_type
            primary {
              background
              title {
                text
              }
              description {
                text
              }
            }
            items {
              headshot {
                gatsbyImageData(width: 800)
                alt
                url
              }
              name {
                text
              }
              role
              content {
                html
              }
            }
          }
          ... on PrismicPageDataBodyCalendar {
            primary {
              background
              title {
                text
              }
              no_events_message {
                html
              }
            }
            slice_type
            items {
              title {
                text
              }
              date
              details {
                html
              }
              button_label
              button_link {
                url
              }
            }
          }
        }
        seo_title {
          text
        }
        seo_description {
          text
        }
        seo_image {
          url
          alt
        }
      }
    }
  }
`;

export function Head({ data }) {
  const PAGE = (data.prismicPage && data.prismicPage.data) || {};
  return (
    <Seo
      title={PAGE.seo_title && PAGE.seo_title.text}
      description={PAGE.seo_description && PAGE.seo_description.text}
      imageOpenGraph={PAGE.seo_image && PAGE.seo_image.url}
      imageAlt={PAGE.seo_image && PAGE.seo_image.alt}
    />
  );
}

export default withPrismicPreview(PageTemplate);
